import type { AlignmentRelation } from "@exploitlens/ui-contracts";

import { formatRelation } from "../../lib/format";

/**
 * `AlignmentRelation` is a structural fact about the call trees (this node
 * moved / appeared / disappeared / changed shape between commits) -- not a
 * security verdict. It deliberately never borrows the loss/preserved
 * red/green vocabulary reserved for gate and case verdicts, so a reader
 * never mistakes "this call was removed" for "this removal is good news" (it
 * frequently isn't the whole story; the gates say what actually matters).
 */
const PRESENTATION: Record<AlignmentRelation, { symbol: string; border: string }> = {
  match: { symbol: "=", border: "border-transparent" },
  insert: { symbol: "+", border: "border-accent" },
  delete: { symbol: "−", border: "border-rule-strong" },
  replace: { symbol: "≈", border: "border-rule-strong" },
  ambiguous: { symbol: "?", border: "border-rule-strong border-dashed" },
};

export function RelationBadge({ relation }: { relation: AlignmentRelation }) {
  if (relation === "match") return null;
  const p = PRESENTATION[relation];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border ${p.border} px-1.5 py-0.5 text-xs text-ink-muted`}
    >
      <span aria-hidden="true">{p.symbol}</span>
      {formatRelation(relation)}
    </span>
  );
}
